import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

type PortType = 'in' | 'out';
type PortShape = 'circle' | 'diamond';

type NodeTemplateKey =
  | 'telegramTrigger'
  | 'portfolioTrigger'
  | 'switch'
  | 'telegram'
  | 'voiceText'
  | 'edit'
  | 'assistant'
  | 'model'
  | 'memory'
  | 'calendar'
  | 'gmail'
  | 'hacker'
  | 'tool'
  | 'borsefrankfurt'
  | 'xetra'
  | 'tradegate'
  | 'onvista'
  | 'finanzennet'
  | 'refinitiv'
  | 'bloomberg';



interface Point {
  x: number;
  y: number;
}

interface FlowNode {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  sub: string;
  icon: string;
  ports: string[];
  circle?: boolean;
  labelTop?: string;
  lightning?: boolean;
}

interface FlowEdge {
  from: string;
  to: string;
  type: 'solid' | 'dashed';
  fromPort: string;
  toPort: string;
  label?: string;
}

interface FlowPort {
  name: string;
  type: PortType;
  x: number;
  y: number;
  shape: PortShape;
}

interface PortHit {
  node: FlowNode;
  port: FlowPort;
  point: Point;
}

interface NodeTemplate {
  w: number;
  h: number;
  title: string;
  sub?: string;
  icon: string;
  ports?: string[];
  circle?: boolean;
  labelTop?: string;
  lightning?: boolean;
}

@Component({
  selector: 'app-flow-editor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flow-editor.component.html',
  styleUrls: ['./flow-editor.component.scss'],
})

export class FlowEditorComponent implements AfterViewInit {
  @ViewChild('flowCanvas', { static: true })
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  paletteItems: Array<{ key: NodeTemplateKey; label: string; badge: string }> = [
    { key: 'portfolioTrigger', label: 'Portfolio Trigger', badge: 'T' },
    { key: 'switch', label: 'Switch', badge: 'S' },
    { key: 'telegram', label: 'Telegram', badge: 'T' },
    { key: 'voiceText', label: 'Voice to Text', badge: 'AI' },
    { key: 'edit', label: 'Edit Fields', badge: 'E' },
    { key: 'assistant', label: 'Agent Assistant', badge: 'A' },
    { key: 'model', label: 'OpenAI Model', badge: 'M' },
    { key: 'memory', label: 'Memory', badge: 'DB' },
    { key: 'calendar', label: 'Calendar', badge: '31' },
    { key: 'gmail', label: 'Gmail', badge: 'G' },
    { key: 'hacker', label: 'Hacker News', badge: 'Y' },
    { key: 'tool', label: 'Tool', badge: '⊞' },
    { key: 'borsefrankfurt', label: 'Börse Frankfurt', badge: 'BO' },
    { key: 'xetra', label: 'Xetra', badge: 'XT' },
    { key: 'tradegate', label: 'Tradegate', badge: 'TG' },
    { key: 'onvista', label: 'Onvista', badge: 'VS' },
    { key: 'finanzennet', label: 'Finanzen.net', badge: 'FNZ' },
    { key: 'refinitiv', label: 'Refinitiv', badge: 'REF' },
    { key: 'bloomberg', label: 'Bloomberg', badge: 'BLM' },
  ];


  paletteDrag: { key: NodeTemplateKey; label: string; x: number; y: number } | null = null;
  contextNode: FlowNode | null = null;
  menuPosition: Point = { x: 0, y: 0 };
  isDraggingCanvas = false;
  connecting: { fromNode: FlowNode; fromPort: string; current: Point } | null = null;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private dpr = 1;
  private scale = 1;
  private offset: Point = { x: 0, y: 0 };
  private draggingNode: FlowNode | null = null;
  private panning = false;
  private dragOffset: Point = { x: 0, y: 0 };
  private lastPointer: Point = { x: 0, y: 0 };
  private pointerWorld: Point = { x: 0, y: 0 };
  private hoverNode: FlowNode | null = null;
  private hoverPort: PortHit | null = null;
  private selectedNode: FlowNode | null = null;
  private nodeCounter = 100;

  private templates: Record<NodeTemplateKey, NodeTemplate> = {
    portfolioTrigger: {
      w: 72,
      h: 66,
      title: 'Portfolio Trigger',
      sub: 'Updates: message',
      icon: 'telegram',
      ports: ['out'],
      lightning: true,
    },
    telegramTrigger: {
      w: 72,
      h: 66,
      title: 'Telegram Trigger',
      sub: 'Updates: message',
      icon: 'telegram',
      ports: ['out'],
      lightning: true,
    },
    switch: {
      w: 72,
      h: 66,
      title: 'Switch',
      sub: 'mode: Rules',
      icon: 'switch',
      ports: ['in', 'audio', 'text', 'error'],
    },
    telegram: {
      w: 72,
      h: 72,
      title: 'Telegram',
      sub: 'sendMessage: message',
      icon: 'telegram',
      ports: ['in', 'out'],
    },
    voiceText: {
      w: 72,
      h: 72,
      title: 'Voice to text',
      sub: 'Transcribe Recording',
      icon: 'openai',
      ports: ['in', 'out'],
    },
    edit: {
      w: 72,
      h: 72,
      title: 'Edit Fields',
      sub: 'manual',
      icon: 'edit',
      ports: ['in', 'out'],
    },
    assistant: {
      w: 185,
      h: 72,
      title: 'Mia - Personal|Assistant',
      sub: 'Tools Agent',
      icon: 'robot',
      ports: ['in', 'out', 'model', 'memory', 'tool'],
    },
    model: {
      w: 56,
      h: 56,
      title: 'OpenAI Chat Model',
      icon: 'openai',
      circle: true,
      labelTop: 'Model',
    },
    memory: {
      w: 56,
      h: 56,
      title: 'Window Buffer|Memory',
      icon: 'database',
      circle: true,
      labelTop: 'Memory',
    },
    calendar: {
      w: 56,
      h: 56,
      title: 'Calendar',
      sub: 'availability: calendar',
      icon: 'calendar',
      circle: true,
    },
    gmail: {
      w: 56,
      h: 56,
      title: 'Gmail',
      sub: 'getAll: message',
      icon: 'gmail',
      circle: true,
    },
    hacker: {
      w: 56,
      h: 56,
      title: 'Hacker News',
      sub: 'getAll: all',
      icon: 'hn',
      circle: true,
    },
    tool: {
      w: 56,
      h: 56,
      title: 'new_tool',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },
    refinitiv :{
      w: 56,
      h: 56,
      title: 'Refinitiv',
      icon: 'refinitiv',
      circle: true,
      labelTop: 'Tool',
    },bloomberg :{
      w: 56,
      h: 56,
      title: 'Bloomberg',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },
    borsefrankfurt :{
      w: 56,
      h: 56,
      title: 'Börse frankfurt',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },
    finanzennet :{
      w: 56,
      h: 56,
      title: 'Finanzen.net',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },onvista :{
      w: 56,
      h: 56,
      title: 'Onvista',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },
    tradegate :{
      w: 56,
      h: 56,
      title: 'Trade gate',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    },
    xetra :{
      w: 56,
      h: 56,
      title: 'Xetra',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    }
  };

  private nodes: FlowNode[] = [
    this.makeNode('trigger', 30, 170, this.templates.portfolioTrigger),
    this.makeNode('switch', 218, 169, this.templates.switch),
    this.makeNode('getvoice', 448, 39, {
      w: 72,
      h: 72,
      title: 'Get the voice',
      sub: 'get: file',
      icon: 'telegram',
      ports: ['in', 'out'],
    }),
    this.makeNode('voicetext', 607, 39, this.templates.voiceText),
    this.makeNode('editfields', 448, 183, this.templates.edit),
    this.makeNode('assistant', 807, 183, this.templates.assistant),
    this.makeNode('telegram', 1267, 183, this.templates.telegram),
    this.makeNode('bloomberg', 391, 414, this.templates.bloomberg),
    this.makeNode('memory', 506, 414, this.templates.memory),
    this.makeNode('calendar', 593, 414, this.templates.calendar),
    this.makeNode('refinitiv', 680, 414, this.templates.refinitiv),
    this.makeNode('hacker', 793, 414, this.templates.hacker),
    this.makeNode('summarize', 923, 414, {
      w: 56,
      h: 56,
      title: 'summarize_article',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    }),
    this.makeNode('ottawa', 1053, 414, {
      w: 56,
      h: 56,
      title: 'ottawa_news',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    }),
    this.makeNode('ircc', 1152, 414, {
      w: 56,
      h: 56,
      title: 'IRCC News',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    }),
    this.makeNode('trip', 1253, 414, {
      w: 56,
      h: 56,
      title: 'trip_planner',
      icon: 'tool',
      circle: true,
      labelTop: 'Tool',
    }),
  ];

  private edges: FlowEdge[] = [
    { from: 'trigger', to: 'switch', type: 'solid', fromPort: 'out', toPort: 'in' },
    { from: 'switch', to: 'getvoice', label: 'Audio', type: 'solid', fromPort: 'audio', toPort: 'in' },
    { from: 'getvoice', to: 'voicetext', type: 'solid', fromPort: 'out', toPort: 'in' },
    { from: 'voicetext', to: 'assistant', type: 'solid', fromPort: 'out', toPort: 'in' },
    { from: 'switch', to: 'editfields', label: 'Text', type: 'solid', fromPort: 'text', toPort: 'in' },
    { from: 'editfields', to: 'assistant', type: 'solid', fromPort: 'out', toPort: 'in' },
    { from: 'assistant', to: 'portfolioTrigger', type: 'solid', fromPort: 'out', toPort: 'in' },
    { from: 'bloomberg', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'bloomberg' },
    { from: 'memory', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'memory' },
    { from: 'calendar', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'refinitiv', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'hacker', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'summarize', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'ottawa', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'ircc', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
    { from: 'trip', to: 'assistant', type: 'dashed', fromPort: 'out', toPort: 'tool' },
  ];

  ngAfterViewInit(): void {
    this.canvas = this.canvasRef.nativeElement;

    const context = this.canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not create canvas context.');
    }

    this.ctx = context;
    this.resizeCanvas();
    this.centerView();
    this.draw();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.canvas) return;
    this.resizeCanvas();
    this.draw();
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.paletteDrag) return;
    this.paletteDrag = { ...this.paletteDrag, x: event.clientX, y: event.clientY };
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(event: PointerEvent): void {
    if (!this.paletteDrag) return;

    const rect = this.canvas.getBoundingClientRect();
    const overCanvas =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    const overPalette = (event.target as HTMLElement | null)?.closest('.palette');

    if (overCanvas && !overPalette) {
      const world = this.screenToWorld({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      this.createNodeFromTemplate(this.paletteDrag.key, world.x, world.y);
    }

    this.paletteDrag = null;
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.node-menu') && target !== this.canvas) {
      this.hideNodeMenu();
    }
  }

  startPaletteDrag(event: PointerEvent, key: NodeTemplateKey, label: string): void {
    event.preventDefault();
    this.hideNodeMenu();
    this.paletteDrag = { key, label, x: event.clientX, y: event.clientY };
  }

  fitView(): void {
    const bounds = this.getBounds();
    const pad = 90;
    const viewW = this.canvas.width / this.dpr;
    const viewH = this.canvas.height / this.dpr;
    this.scale = Math.max(
      0.45,
      Math.min(1.35, Math.min((viewW - pad * 2) / bounds.w, (viewH - pad * 2) / bounds.h)),
    );
    this.offset.x = pad - bounds.x * this.scale;
    this.offset.y = pad - bounds.y * this.scale;
    this.draw();
  }

  centerViewAndDraw(): void {
    this.centerView();
    this.draw();
  }

  clearConnections(): void {
    this.edges = [];
    this.hideNodeMenu();
    this.draw();
  }

  onCanvasContextMenu(event: MouseEvent): void {
    event.preventDefault();

    const world = this.screenToWorld(this.canvasPoint(event));
    const node = this.hitTestNode(world.x, world.y);

    if (!node) {
      this.hideNodeMenu();
      return;
    }

    this.contextNode = node;
    this.selectedNode = node;
    this.menuPosition = { x: event.clientX, y: event.clientY };
    this.draw();
  }

  deleteContextNode(): void {
    if (!this.contextNode) return;

    const id = this.contextNode.id;
    this.nodes = this.nodes.filter((node) => node.id !== id);
    this.edges = this.edges.filter((edge) => edge.from !== id && edge.to !== id);

    if (this.selectedNode?.id === id) this.selectedNode = null;
    if (this.hoverNode?.id === id) this.hoverNode = null;

    this.draggingNode = null;
    this.connecting = null;
    this.hideNodeMenu();
    this.draw();
  }

  onCanvasPointerDown(event: PointerEvent): void {
    if (event.button === 2) return;

    this.hideNodeMenu();

    const point = this.canvasPoint(event);
    const world = this.screenToWorld(point);
    this.pointerWorld = world;

    const portHit = this.hitTestPort(world.x, world.y);
    const nodeHit = this.hitTestNode(world.x, world.y);

    this.canvas.setPointerCapture(event.pointerId);
    this.lastPointer = point;

    if (portHit) {
      this.connecting = {
        fromNode: portHit.node,
        fromPort: portHit.port.name,
        current: world,
      };
      this.selectedNode = portHit.node;
    } else if (nodeHit) {
      this.draggingNode = nodeHit;
      this.selectedNode = nodeHit;
      this.dragOffset = {
        x: world.x - nodeHit.x,
        y: world.y - nodeHit.y,
      };
      this.isDraggingCanvas = true;
    } else {
      this.panning = true;
      this.selectedNode = null;
      this.isDraggingCanvas = true;
    }

    this.draw();
  }

  onCanvasPointerMove(event: PointerEvent): void {
    const point = this.canvasPoint(event);
    const world = this.screenToWorld(point);
    this.pointerWorld = world;

    if (this.connecting) {
      this.connecting.current = world;
      this.hoverPort = this.hitTestPort(world.x, world.y, this.connecting.fromNode.id);
      this.hoverNode = this.hoverPort?.node ?? this.hitTestNode(world.x, world.y);
      this.draw();
      return;
    }

    if (this.draggingNode) {
      this.draggingNode.x = world.x - this.dragOffset.x;
      this.draggingNode.y = world.y - this.dragOffset.y;
      this.draw();
      return;
    }

    if (this.panning) {
      this.offset.x += point.x - this.lastPointer.x;
      this.offset.y += point.y - this.lastPointer.y;
      this.lastPointer = point;
      this.draw();
      return;
    }

    const nextPort = this.hitTestPort(world.x, world.y);
    const nextHover = nextPort?.node ?? this.hitTestNode(world.x, world.y);

    if (nextHover !== this.hoverNode || nextPort !== this.hoverPort) {
      this.hoverNode = nextHover;
      this.hoverPort = nextPort;
      this.draw();
    }
  }

  onCanvasPointerUp(event: PointerEvent): void {
    const world = this.screenToWorld(this.canvasPoint(event));

    if (this.connecting) {
      const targetPort = this.hitTestPort(world.x, world.y, this.connecting.fromNode.id);
      const targetNode = targetPort?.node ?? this.hitTestNode(world.x, world.y);

      if (targetNode && targetNode.id !== this.connecting.fromNode.id) {
        this.edges.push({
          from: this.connecting.fromNode.id,
          to: targetNode.id,
          type: 'solid',
          fromPort: this.connecting.fromPort,
          toPort: targetPort?.port.name ?? this.bestInputPort(targetNode, this.connecting.fromNode),
        });
      }
    }

    try {
      this.canvas.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be released.
    }

    this.draggingNode = null;
    this.connecting = null;
    this.panning = false;
    this.hoverPort = null;
    this.isDraggingCanvas = false;
    this.draw();
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.hideNodeMenu();

    const point = this.canvasPoint(event);
    const before = this.screenToWorld(point);

    this.scale = Math.max(
      0.35,
      Math.min(2.2, this.scale * (event.deltaY < 0 ? 1.08 : 0.92)),
    );

    const after = this.screenToWorld(point);
    this.offset.x += (after.x - before.x) * this.scale;
    this.offset.y += (after.y - before.y) * this.scale;

    this.draw();
  }

  private makeNode(id: string, x: number, y: number, template: NodeTemplate): FlowNode {
    return {
      id,
      x,
      y,
      w: template.w,
      h: template.h,
      title: template.title,
      sub: template.sub ?? '',
      icon: template.icon,
      ports: template.ports ? [...template.ports] : [],
      circle: !!template.circle,
      labelTop: template.labelTop ?? '',
      lightning: !!template.lightning,
    };
  }

  private createNodeFromTemplate(key: NodeTemplateKey, worldX: number, worldY: number): void {
    const template = this.templates[key];
    const id = `${key}_${this.nodeCounter++}`;
    const node = this.makeNode(id, worldX - template.w / 2, worldY - template.h / 2, template);

    if (key === 'tool') {
      node.title = `tool_${this.nodeCounter - 100}`;
    }

    this.nodes.push(node);
    this.selectedNode = node;
    this.hoverNode = node;
    this.draw();
  }

  private hideNodeMenu(): void {
    this.contextNode = null;
  }

  private resizeCanvas(): void {
    this.dpr = window.devicePixelRatio || 1;

    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.floor(rect.width * this.dpr));
    this.canvas.height = Math.max(1, Math.floor(rect.height * this.dpr));

    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private getBounds(): { x: number; y: number; w: number; h: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    this.nodes.forEach((node) => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + node.w);
      maxY = Math.max(maxY, node.y + node.h + 48);
    });

    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    };
  }

  private centerView(): void {
    const bounds = this.getBounds();
    const viewW = this.canvas.width / this.dpr;
    const viewH = this.canvas.height / this.dpr;

    this.offset.x = (viewW - bounds.w * this.scale) / 2 - bounds.x * this.scale;
    this.offset.y = (viewH - bounds.h * this.scale) / 2 - bounds.y * this.scale;
  }

  private canvasPoint(event: MouseEvent | PointerEvent | WheelEvent): Point {
    const rect = this.canvas.getBoundingClientRect();

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private screenToWorld(point: Point): Point {
    return {
      x: (point.x - this.offset.x) / this.scale,
      y: (point.y - this.offset.y) / this.scale,
    };
  }

  private hitTestNode(x: number, y: number): FlowNode | null {
    for (let i = this.nodes.length - 1; i >= 0; i--) {
      const node = this.nodes[i];

      if (node.circle) {
        const cx = node.x + node.w / 2;
        const cy = node.y + node.h / 2;

        if (Math.hypot(x - cx, y - cy) <= node.w / 2 + 10) {
          return node;
        }
      } else if (x >= node.x && x <= node.x + node.w && y >= node.y && y <= node.y + node.h) {
        return node;
      }
    }

    return null;
  }

  private hitTestPort(x: number, y: number, excludeNodeId?: string): PortHit | null {
    const radius = 11;

    for (let i = this.nodes.length - 1; i >= 0; i--) {
      const node = this.nodes[i];

      if (excludeNodeId && node.id === excludeNodeId) {
        continue;
      }

      for (const port of this.getPorts(node)) {
        if (Math.hypot(x - port.x, y - port.y) <= radius) {
          return {
            node,
            port,
            point: { x: port.x, y: port.y },
          };
        }
      }
    }

    return null;
  }

  private getPorts(node: FlowNode): FlowPort[] {
    const ports: FlowPort[] = [];

    if (node.circle) {
      ports.push({
        name: 'out',
        type: 'out',
        x: node.x + node.w / 2,
        y: node.y,
        shape: 'diamond',
      });
      ports.push({
        name: 'in',
        type: 'in',
        x: node.x + node.w / 2,
        y: node.y + node.h,
        shape: 'diamond',
      });
      return ports;
    }

    if (node.ports.includes('in')) {
      ports.push({
        name: 'in',
        type: 'in',
        x: node.x,
        y: node.y + node.h / 2,
        shape: 'circle',
      });
    }

    if (node.ports.includes('out')) {
      ports.push({
        name: 'out',
        type: 'out',
        x: node.x + node.w,
        y: node.y + node.h / 2,
        shape: 'circle',
      });
    }

    if (node.id.startsWith('switch') || node.icon === 'switch') {
      ports.push({
        name: 'audio',
        type: 'out',
        x: node.x + node.w,
        y: node.y + node.h * 0.26,
        shape: 'circle',
      });
      ports.push({
        name: 'text',
        type: 'out',
        x: node.x + node.w,
        y: node.y + node.h * 0.52,
        shape: 'circle',
      });
      ports.push({
        name: 'error',
        type: 'out',
        x: node.x + node.w,
        y: node.y + node.h * 0.78,
        shape: 'circle',
      });
    }

    if (node.id.startsWith('assistant') || node.icon === 'robot') {
      ports.push({
        name: 'model',
        type: 'in',
        x: node.x + 36,
        y: node.y + node.h,
        shape: 'diamond',
      });
      ports.push({
        name: 'memory',
        type: 'in',
        x: node.x + 110,
        y: node.y + node.h,
        shape: 'diamond',
      });
      ports.push({
        name: 'tool',
        type: 'in',
        x: node.x + 148,
        y: node.y + node.h,
        shape: 'diamond',
      });
    }

    return ports;
  }

  private bestInputPort(target: FlowNode, fromNode: FlowNode): string {
    if ((target.id.startsWith('assistant') || target.icon === 'robot') && fromNode.circle) {
      if (fromNode.id.startsWith('model')) return 'model';
      if (fromNode.id.startsWith('memory')) return 'memory';
      return 'tool';
    }

    return 'in';
  }

  private portPoint(node: FlowNode, preferredType: PortType, portName: string): Point {
    const ports = this.getPorts(node);

    const exact = ports.find((port) => port.name === portName);
    if (exact) return { x: exact.x, y: exact.y };

    const preferred = ports.find((port) => port.type === preferredType);
    if (preferred) return { x: preferred.x, y: preferred.y };

    return {
      x: node.x + node.w / 2,
      y: node.y + node.h / 2,
    };
  }

  private draw(): void {
    if (!this.ctx) return;

    const width = this.canvas.width / this.dpr;
    const height = this.canvas.height / this.dpr;

    this.ctx.clearRect(0, 0, width, height);

    this.ctx.save();
    this.ctx.translate(this.offset.x, this.offset.y);
    this.ctx.scale(this.scale, this.scale);

    this.edges.forEach((edge) => this.drawEdge(edge));

    if (this.connecting) {
      this.drawPreviewConnection();
    }

    this.nodes.forEach((node) => this.drawNode(node));

    this.ctx.restore();
  }

  private findNode(id: string): FlowNode | undefined {
    return this.nodes.find((node) => node.id === id);
  }

  private drawEdge(edge: FlowEdge): void {
    const from = this.findNode(edge.from);
    const to = this.findNode(edge.to);

    if (!from || !to) return;

    const start = this.portPoint(from, 'out', edge.fromPort || 'out');
    const end = this.portPoint(to, 'in', edge.toPort || 'in');

    this.drawBezier(start, end, edge.type === 'dashed');

    if (edge.label) {
      this.drawEdgeLabel(edge.label, start, end);
    }
  }

  private drawPreviewConnection(): void {
    if (!this.connecting) return;

    const start = this.portPoint(this.connecting.fromNode, 'out', this.connecting.fromPort);
    const end = this.hoverPort ? this.hoverPort.point : this.pointerWorld;

    this.drawBezier(start, end, false, true);
  }

  private drawBezier(start: Point, end: Point, dashed: boolean, preview = false): void {
    const dx = Math.max(70, Math.abs(end.x - start.x) * 0.45);

    this.ctx.save();
    this.ctx.strokeStyle = preview
      ? 'rgba(96,165,250,.95)'
      : dashed
        ? 'rgba(142,150,163,.52)'
        : 'rgba(172,181,193,.82)';
    this.ctx.lineWidth = preview ? 3 : 2;
    this.ctx.setLineDash(dashed || preview ? [6, 6] : []);

    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.bezierCurveTo(start.x + dx, start.y, end.x - dx, end.y, end.x, end.y);
    this.ctx.stroke();

    this.ctx.setLineDash([]);
    this.drawArrow(start, end, dashed && !preview);
    this.ctx.restore();
  }

  private drawArrow(start: Point, end: Point, muted: boolean): void {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const size = 7;

    this.ctx.fillStyle = muted ? 'rgba(142,150,163,.68)' : 'rgba(190,199,211,.9)';
    this.ctx.beginPath();
    this.ctx.moveTo(end.x, end.y);
    this.ctx.lineTo(
      end.x - size * Math.cos(angle - Math.PI / 6),
      end.y - size * Math.sin(angle - Math.PI / 6),
    );
    this.ctx.lineTo(
      end.x - size * Math.cos(angle + Math.PI / 6),
      end.y - size * Math.sin(angle + Math.PI / 6),
    );
    this.ctx.closePath();
    this.ctx.fill();
  }

  private drawEdgeLabel(label: string, start: Point, end: Point): void {
    const x = start.x + (end.x - start.x) * 0.28;
    const y = start.y + (end.y - start.y) * 0.35 - 12;

    this.ctx.save();
    this.ctx.font = '11px Inter, sans-serif';
    this.ctx.fillStyle = 'rgba(200,207,218,.88)';
    this.ctx.fillText(label, x, y);
    this.ctx.restore();
  }

  private drawNode(node: FlowNode): void {
    if (node.circle) {
      this.drawCircleNode(node);
    } else {
      this.drawRectNode(node);
    }

    this.drawLabels(node);
  }

  private drawRectNode(node: FlowNode): void {
    const active = node === this.hoverNode || node === this.selectedNode;

    this.ctx.save();
    this.roundRect(node.x, node.y, node.w, node.h, 6);
    this.ctx.fillStyle = active ? '#303642' : '#262b34';
    this.ctx.strokeStyle = active ? '#b8c2d1' : '#7b8492';
    this.ctx.lineWidth = active ? 2 : 1.25;
    this.ctx.fill();
    this.ctx.stroke();

    if (node.lightning) {
      this.ctx.fillStyle = '#ff5b35';
      this.ctx.font = 'bold 20px Inter, sans-serif';
      this.ctx.fillText('!', node.x - 16, node.y + 38);
    }

    this.drawIcon(node.icon, node.x + node.w / 2, node.y + node.h / 2, 22);
    this.drawPorts(node);
    this.ctx.restore();
  }

  private drawCircleNode(node: FlowNode): void {
    const active = node === this.hoverNode || node === this.selectedNode;
    const cx = node.x + node.w / 2;
    const cy = node.y + node.h / 2;

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, node.w / 2, 0, Math.PI * 2);
    this.ctx.fillStyle = active ? '#303642' : '#252a33';
    this.ctx.strokeStyle = active ? '#c3cbd8' : '#6f7885';
    this.ctx.lineWidth = active ? 2 : 1.25;
    this.ctx.fill();
    this.ctx.stroke();

    this.drawIcon(node.icon, cx, cy, 19);
    this.drawPorts(node);
    this.ctx.restore();
  }

  private drawPorts(node: FlowNode): void {
    for (const port of this.getPorts(node)) {
      const isHot =
        this.hoverPort?.node.id === node.id && this.hoverPort.port.name === port.name;

      if (port.shape === 'diamond') {
        this.drawDiamond(port.x, port.y, isHot ? 9 : 7, isHot ? '#60a5fa' : '#9ca3af');
      } else {
        this.drawPort(port.x, port.y, isHot);
      }
    }

    if (node.icon === 'switch') {
      this.drawPlusBox(node.x + node.w + 56, node.y + node.h * 0.78 - 12);
    }

    if (node.icon === 'robot') {
      this.drawPlusBox(node.x + 148, node.y + node.h + 46);
    }

    if (node.title === 'Telegram') {
      this.drawPlusBox(node.x + node.w + 48, node.y + node.h / 2 - 12);
    }

    if (node.title === 'PortfolioTrigger') {
      this.drawPlusBox(node.x + node.w + 48, node.y + node.h / 2 - 12);
    }
  }

  private drawPort(x: number, y: number, hot: boolean): void {
    this.ctx.beginPath();
    this.ctx.arc(x, y, hot ? 8 : 6, 0, Math.PI * 2);
    this.ctx.fillStyle = hot ? '#60a5fa' : '#a9b0bd';
    this.ctx.fill();
  }

  private drawDiamond(x: number, y: number, size: number, color: string): void {
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.moveTo(x, y - size);
    this.ctx.lineTo(x + size, y);
    this.ctx.lineTo(x, y + size);
    this.ctx.lineTo(x - size, y);
    this.ctx.closePath();
    this.ctx.fill();
  }

  private drawPlusBox(x: number, y: number): void {
    this.ctx.save();
    this.roundRect(x, y, 16, 16, 4);
    this.ctx.strokeStyle = '#8d96a5';
    this.ctx.lineWidth = 1.3;
    this.ctx.stroke();
    this.ctx.fillStyle = '#8d96a5';
    this.ctx.font = 'bold 16px Inter, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('+', x + 8, y + 8);
    this.ctx.restore();
  }

  private drawLabels(node: FlowNode): void {
    const cx = node.x + node.w / 2;
    const y = node.y + node.h + 17;
    const lines = node.title.split('|');

    this.ctx.save();
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'top';

    if (node.labelTop) {
      this.ctx.font = '10px Inter, sans-serif';
      this.ctx.fillStyle = '#9099a8';
      this.ctx.fillText(node.labelTop, cx, node.y - 21);
    }

    this.ctx.font = '12px Inter, sans-serif';
    this.ctx.fillStyle = '#eef2f7';
    lines.forEach((line, index) => this.ctx.fillText(line, cx, y + index * 14));

    if (node.sub) {
      this.ctx.font = '10px Inter, sans-serif';
      this.ctx.fillStyle = '#8f98a7';
      this.ctx.fillText(node.sub, cx, y + lines.length * 14 + 2);
    }

    this.ctx.restore();
  }

  private drawIcon(icon: string, cx: number, cy: number, size: number): void {
    this.ctx.save();
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    if (icon === 'telegram') {
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, size * 0.62, 0, Math.PI * 2);
      this.ctx.fillStyle = '#32a9dc';
      this.ctx.fill();
      this.ctx.fillStyle = '#eaf8ff';
      this.ctx.font = 'bold 14px Inter, sans-serif';
      this.ctx.fillText('T', cx, cy + 1);
    }else if (icon === 'refinitiv') {
        const r = size * 0.62;

        // dark background
        this.ctx.beginPath();
        this.ctx.arc(cx, cy, r, 0, Math.PI * 2);
        this.ctx.fillStyle = '#2f3133';
        this.ctx.fill();

        // white uploaded-style mark
        this.ctx.strokeStyle = '#f2f2f2';
        this.ctx.lineWidth = Math.max(2, size * 0.08);
        this.ctx.lineCap = 'square';
        this.ctx.lineJoin = 'miter';

        const left = cx - r * 0.65;
        const right = cx + r * 0.68;
        const top = cy - r * 0.62;
        const mid = cy - r * 0.02;
        const bottom = cy + r * 0.62;

        this.ctx.beginPath();

        // top line + left vertical
        this.ctx.moveTo(right, top);
        this.ctx.lineTo(left, top);
        this.ctx.lineTo(left, bottom);

        // middle horizontal
        this.ctx.moveTo(left, mid);
        this.ctx.lineTo(right, mid);

        // diagonal
        this.ctx.moveTo(cx - r * 0.08, mid);
        this.ctx.lineTo(right, bottom);

        this.ctx.stroke();
    } else if (icon === 'openai') {
      this.ctx.strokeStyle = '#f1f5f9';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, 12, 0, Math.PI * 2);
      this.ctx.moveTo(cx - 9, cy - 9);
      this.ctx.lineTo(cx + 9, cy + 9);
      this.ctx.moveTo(cx + 9, cy - 9);
      this.ctx.lineTo(cx - 9, cy + 9);
      this.ctx.stroke();
    } else if (icon === 'switch') {
      this.ctx.strokeStyle = '#5fb7f2';
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.moveTo(cx - 14, cy - 10);
      this.ctx.lineTo(cx + 14, cy - 10);
      this.ctx.moveTo(cx - 14, cy);
      this.ctx.lineTo(cx + 14, cy);
      this.ctx.moveTo(cx - 14, cy + 10);
      this.ctx.lineTo(cx + 14, cy + 10);
      this.ctx.stroke();
    } else if (icon === 'edit') {
      this.ctx.fillStyle = '#7c8cff';
      this.ctx.translate(cx, cy);
      this.ctx.rotate(-Math.PI / 4);
      this.roundRect(-5, -18, 10, 36, 3);
      this.ctx.fill();
      this.ctx.fillStyle = '#b7c0ff';
      this.ctx.fillRect(-5, -21, 10, 5);
    } else if (icon === 'robot') {
      this.ctx.fillStyle = '#eef2f7';
      this.roundRect(cx - 12, cy - 10, 24, 22, 5);
      this.ctx.fill();
      this.ctx.fillRect(cx - 2, cy - 18, 4, 8);
      this.ctx.beginPath();
      this.ctx.arc(cx, cy - 20, 3, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.fillStyle = '#262b34';
      this.ctx.beginPath();
      this.ctx.arc(cx - 6, cy, 2, 0, Math.PI * 2);
      this.ctx.arc(cx + 6, cy, 2, 0, Math.PI * 2);
      this.ctx.fill();
    } else if (icon === 'database') {
      this.ctx.strokeStyle = '#f1f5f9';
      this.ctx.lineWidth = 2;
      for (let i = 0; i < 3; i++) {
        this.ctx.beginPath();
        this.ctx.ellipse(cx, cy - 10 + i * 9, 12, 5, 0, 0, Math.PI * 2);
        this.ctx.stroke();
      }
    } else if (icon === 'calendar') {
      this.ctx.fillStyle = '#4285f4';
      this.roundRect(cx - 12, cy - 12, 24, 24, 4);
      this.ctx.fill();
      this.ctx.fillStyle = '#fff';
      this.ctx.fillRect(cx - 12, cy - 6, 24, 18);
      this.ctx.fillStyle = '#34a853';
      this.ctx.font = 'bold 11px Inter';
      this.ctx.fillText('31', cx, cy + 3);
    } else if (icon === 'gmail') {
      this.ctx.fillStyle = '#fff';
      this.roundRect(cx - 13, cy - 10, 26, 20, 3);
      this.ctx.fill();
      this.ctx.strokeStyle = '#ea4335';
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.moveTo(cx - 12, cy - 8);
      this.ctx.lineTo(cx, cy + 2);
      this.ctx.lineTo(cx + 12, cy - 8);
      this.ctx.stroke();
    } else if (icon === 'hn') {
      this.ctx.fillStyle = '#ff6600';
      this.roundRect(cx - 12, cy - 12, 24, 24, 5);
      this.ctx.fill();
      this.ctx.fillStyle = '#fff';
      this.ctx.font = 'bold 16px Inter';
      this.ctx.fillText('Y', cx, cy + 1);
    } else if (icon === 'tool') {
      this.ctx.strokeStyle = '#f1f5f9';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(cx, cy - 14);
      this.ctx.lineTo(cx, cy - 4);
      this.ctx.moveTo(cx - 12, cy + 2);
      this.ctx.lineTo(cx + 12, cy + 2);
      this.ctx.moveTo(cx - 8, cy + 2);
      this.ctx.lineTo(cx - 8, cy + 12);
      this.ctx.moveTo(cx + 8, cy + 2);
      this.ctx.lineTo(cx + 8, cy + 12);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  private roundRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ): void {
    const r = Math.min(radius, width / 2, height / 2);

    this.ctx.beginPath();
    this.ctx.moveTo(x + r, y);
    this.ctx.arcTo(x + width, y, x + width, y + height, r);
    this.ctx.arcTo(x + width, y + height, x, y + height, r);
    this.ctx.arcTo(x, y + height, x, y, r);
    this.ctx.arcTo(x, y, x + width, y, r);
    this.ctx.closePath();
  }
}
